/*
 * Copyright © 2017 - 2019 Atilika Inc. All rights reserved.
 */

import React, {memo, ReactNode} from "react";
import {createUseStyles} from "react-jss";
import {Light as SyntaxHighlighter} from "react-syntax-highlighter";
import java from "react-syntax-highlighter/dist/esm/languages/hljs/java";
import javascript from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import xml from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import tomorrowNight from "react-syntax-highlighter/dist/esm/styles/hljs/tomorrow-night";

SyntaxHighlighter.registerLanguage("xml", xml);
SyntaxHighlighter.registerLanguage("java", java);
SyntaxHighlighter.registerLanguage("javascript", javascript);

const useStyles = createUseStyles({
    root: {
        borderRadius: ".25rem",
        position: "relative"
    }
});

interface CodeProps {
    lang: "xml" | "java" | "javascript";
    children: ReactNode;
}

function clean(str: string): string {
    const base = str.replace(/^\n+/, "");

    let leadSpaces = 0;
    for (let i = 0; i < base.length; i++) {
        if (base.charAt(i) !== " ") {
            break;
        }

        leadSpaces++;
    }

    return base
        .split("\n")
        .map((line) => line.replace(new RegExp(`^[ ]{${leadSpaces}}`), ""))
        .join("\n")
        .replace(/\s+$/, "");
}

/**
 * Used to highlight a block of code.
 * Uses hljs underneath to tokenize code, wrapping in span tags
 * that can be used by hljs them. Also adds line numbers.
 *
 * NB: If the code you are highlighting contains characters that have special meaning in JSX,
 * such as: {, }, <, or >, then you should use a template string to pass the code in.
 *
 * Example:
 * <Code lang="javascript">{`
 *     function hello() {
 *         console.log("this works, despite the curly brackets);
 *     }
 * `}</Code>
 *
 * @param {CodeProps} props
 * @returns {JSX.Element}
 */
function Code(props: CodeProps) {
    const classes = useStyles();
    return (
        <SyntaxHighlighter
            showLineNumbers
            className={classes.root}
            language={props.lang}
            style={tomorrowNight}
        >
            {typeof props.children === "string"
                ? clean(props.children)
                : JSON.stringify(props.children, null, 4)}
        </SyntaxHighlighter>
    );
}

export default memo(Code);
